import axios from "axios";
import { API_URL } from "src/scenes/App";
import { UserKeys } from "src/constants/userDetails";

const getToken = () => localStorage.getItem(UserKeys.TOKEN);

const headers = () => ({
    Authorization: "Bearer " + getToken(),
});

/**
 * LICENSE TRIAL DOCUMENTS
 * These are documents related to onboarding / license flow.
 * They are NOT company user-uploaded documents.
 */

/** Current user (Client) Deletes a license document */
export const deleteLicenseTrialDocument = (uuid) => {
    return axios.delete(
        `${API_URL}/licenseTrial/documents/${uuid}`,
        { headers: headers() }
    );
};

/** Admin user that has view documents permissions Rejects a license document during review */
export const rejectLicenseTrialDocument = (uuid) => {
    return axios.delete(
        `${API_URL}/licenseTrial/manage-documents/${uuid}/reject`,
        { headers: headers() }
    );
};

/** Admin user that has view documents permissions Approves a license document */
export const approveLicenseTrialDocument = (uuid) => {
    return axios.put(
        `${API_URL}/licenseTrial/manage-documents/${uuid}/approve`,
        {},
        { headers: headers() }
    );
};

/** Admin user that has view documents permission Unapproves license document */
export const unapproveLicenseTrialDocument = (uuid) => {
    return axios.put(
        `${API_URL}/licenseTrial/manage-documents/${uuid}/unApprove`,
        {},
        { headers: headers() }
    );
};


/**
 * USER / COMPANY DOCUMENTS
 * These are documents uploaded by employees or clients.
 */

/** Current user (Employee) delete their uploaded document */
export const deleteUserDocument = (uuid) => {
    return axios.delete(
        `${API_URL}/userDocuments/documents/${uuid}`,
        { headers: headers() }
    );
};

/** Admin user that has my user list permissions reject a user document upload */
export const rejectUserDocument = (uuid) => {
    return axios.delete(
        `${API_URL}/company/userDocuments/uploads/${uuid}/reject`,
        { headers: headers() }
    );
};

/** Admin user that has my user list permissions Approves a user document upload */
export const approveUserDocument = (uuid) => {
    return axios.put(
        `${API_URL}/company/userDocuments/uploads/${uuid}/approve`,
        {},
        { headers: headers() }
    );
};

/** Admin user that has my user list permissions Unapproves a user document upload */
export const unapproveUserDocument = (uuid) => {
    return axios.put(
        `${API_URL}/company/userDocuments/uploads/${uuid}/unApprove`,
        {},
        { headers: headers() }
    );
};


/**
 * MANAGE DOCUMENT TYPES (For my user list)
 * These define document categories for company users.
 */

/** Gets all document types for employees*/
export const getUserListDocumentTypes = () => {
    return axios.get(`${API_URL}/company/userDocuments/types`, { headers: headers() });
};

/** Creates a new user document type */
export const createUserListDocumentType = (description) => {
    return axios.post(
        `${API_URL}/company/userDocuments/types`,
        { description },
        { headers: headers() }
    );
};

/** Deletes a document type */
export const deleteUserListDocumentType = (uuid) => {
    return axios.delete(
        `${API_URL}/company/userDocuments/types/${uuid}`,
        { headers: headers() }
    );
};

/** Updates a document type */
export const updateUserListDocumentType = (uuid, description) => {
    return axios.put(
        `${API_URL}/company/userDocuments/types/${uuid}`,
        { description },
        { headers: headers() }
    );
};

/**
 * UPLOAD FUNCTIONS (LICENSE + USER)
 * These handle S3 upload flow via presigned URLs.
 */

/**
 * Upload license trial documents (self-service)
 */
export const uploadDocuments = async ({ requiredDocuments, documents }) => {
    for (let i = 0; i < requiredDocuments.length; i++) {
        const docMeta = requiredDocuments[i];
        const file = documents?.[i];

        if (!file) continue;

        const fileName = file.name;

        if (!fileName) {
            throw new Error("File must have a name");
        }

        const documentsPrefix = docMeta?.companyUuid && !docMeta?.tierUuid
            ? "/userDocuments/documents"
            : "/licenseTrial/documents";

        const res = await axios.get(
            `${API_URL}${documentsPrefix}/${docMeta.uuid}/generateUploadUrl/${encodeURIComponent(fileName)}`,
            { headers: headers() }
        );

        const fullUrl = res.data.url;

        if (!fullUrl) {
            throw new Error("Missing presigned URL");
        }

        const storedUrl = fullUrl.split("?")[0];
        const uploadResponse = await fetch(fullUrl, {
            method: "PUT",
            body: file,
            headers: {
                "Content-Type": file.type
            }
        });

        if (!uploadResponse.ok) {
            throw new Error("Failed to upload file to S3");
        }

        await axios.put(
            `${API_URL}${documentsPrefix}/${docMeta.uuid}`,
            { url: storedUrl },
            { headers: headers() }
        );
    }
};


/**
 * Upload license documents as admin for sales and promotions clients
 */
export const adminUploadDocuments = async ({
    requiredDocuments,
    documents,
    employeeUuid
}) => {
    for (let i = 0; i < requiredDocuments.length; i++) {
        const docMeta = requiredDocuments[i];
        const file = documents?.[i];
        if (!file || file?.uploaded) continue;

        const fileName = file.name;

        if (!fileName) {
            throw new Error("File must have a name");
        }

        const res = await axios.get(
            `${API_URL}/licenseTrial/documents/${docMeta.uuid}/admin/${employeeUuid}/generateUploadUrl/${encodeURIComponent(fileName)}`,
            { headers: headers() }
        );

        const fullUrl = res.data.url;

        if (!fullUrl) {
            throw new Error("Missing presigned URL");
        }
        const storedUrl = fullUrl.split("?")[0];

        const uploadResponse = await fetch(fullUrl, {
            method: "PUT",
            body: file,
            headers: {
                "Content-Type": file.type
            }
        });

        if (!uploadResponse.ok) {
            throw new Error("Failed to upload file to S3");
        }

        await axios.put(
            `${API_URL}/licenseTrial/documents/${docMeta.uuid}/admin/${employeeUuid}`,
            { url: storedUrl },
            { headers: headers() }
        );
    }
};


/**
 * Upload user documents as admin for my user list employees
 */
export const adminUploadUserDocuments = async ({
    requiredDocuments,
    documents,
    employeeUuid
}) => {
    for (let i = 0; i < requiredDocuments.length; i++) {
        const docMeta = requiredDocuments[i];
        const file = documents?.[i];
        if (!file || file?.uploaded) continue;

        const fileName = file.name;

        if (!fileName) {
            throw new Error("File must have a name");
        }

        const res = await axios.get(
            `${API_URL}/company/userDocuments/documents/${docMeta.uuid}/admin/${employeeUuid}/generateUploadUrl/${encodeURIComponent(fileName)}`,
            { headers: headers() }
        );

        const fullUrl = res.data.url;

        if (!fullUrl) {
            throw new Error("Missing presigned URL");
        }
        const storedUrl = fullUrl.split("?")[0];

        const uploadResponse = await fetch(fullUrl, {
            method: "PUT",
            body: file,
            headers: {
                "Content-Type": file.type
            }
        });

        if (!uploadResponse.ok) {
            throw new Error("Failed to upload file to S3");
        }

        await axios.put(
            `${API_URL}/company/userDocuments/documents/${docMeta.uuid}/admin/${employeeUuid}`,
            { url: storedUrl },
            { headers: headers() }
        );
    }
};